import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  AlertComponent,
  BreadcrumbComponent,
  CountryCodeWithPhoneNumber,
  DescriptionInputComponent,
  TextInputComponent,
} from "../../../common/components";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import "./customer-support.scss";
import MUIBoxTable from "../../../common/components/mui-table/mui-box-table";
import { useSetLoading } from "../../../common/hooks/appearance";
import icons from "../../../assets/icons/icons";
import { getIconBasedOnFileExt } from "../../../utils/fetchIconForExtension";
import { UploadFile } from "../../../common/components/upload-file-popup/upload-popup-props";
import { ALLOWED_MIME_TYPES } from "../../../common/components/upload-file-popup/file-upload-dropzoze";
import { convertUTCToLocal, isOnlyCountryCode } from "../../../utils/utils";
import phoneValidation from "../../../common/components/phone-number-input-component/phone-validation";
import { getSupportList, postSupport } from "../../../api/support";
import { AlertMessage } from "../../tenant/tenant-details/tenant-details-types";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";
import { useLogin } from "../../../common/hooks/useUser";

const MAX_FILE_SIZE_MB = 110;
const MAX_FILE_COUNT = 3;
const MAX_FILE_ERROR_KEY = "__max_limit__";

const CustomerSupport: React.FC = () => {
  const breadcrumbLink = [{ label: "Support", link: "#" }];
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [supportHistory, setSupportHistory] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
  });

  const { canWrite } = useResourcePermissions(MODULES.CUSTOMER_SUPPORT);
  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const headers = [
    { key: "ticket_id", label: "Ticket ID" },
    { key: "subject", label: "Subject" },
    { key: "name", label: "Raised By" },
    {
      key: "created_at",
      label: "Date Submitted",
    },
  ];

  const initialValues = {
    name: "",
    email: "",
    subject: "",
    description: "",
    phone: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(3, 'Name must be at least 3 characters').max(50, 'Name must be at most 50 characters').required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    subject: Yup.string().min(3, 'Subject must be at least 3 characters').max(50, 'Subject must be at most 50 characters').required("Subject is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .test(
        "valid-phone-check",
        "Please enter a valid phone number",
        function (value) {
          if (!value) return false;

          if (isOnlyCountryCode(value)) {
            return this.createError({
              message: "Phone number is required",
            });
          }

          if (!phoneValidation(value)) {
            return this.createError({
              message: "Please enter a valid phone number",
            });
          }

          return true;
        }
      ),
    description: Yup.string().min(10, 'Description must be at least 10 characters').required("Description is required"),
  });

  const noResultText = useMemo(() => {
    if (isLoading) {
      return "Loading...";
    }
    return "No records found.";
  }, [isLoading]);
  const formattedData = useMemo(() => {
    if (!supportHistory || supportHistory.length === 0) {
      return [];
    }
    return supportHistory.map((item) => ({
      ...item,
      created_at: convertUTCToLocal(item.created_at, "MM/DD/YYYY"),
    }));
  }, [supportHistory]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiless: any = event.target.files;
    const newFiles: UploadFile[] = [];
    const newErrors: Record<string, string> = {};
    if (!selectedFiless) {
      event.target.value = "";
      return;
    }
    if (files.length + selectedFiless.length > 3) {
      newErrors[MAX_FILE_ERROR_KEY] =
        `You can upload a maximum of ${MAX_FILE_COUNT} files.`;
    }

    [...selectedFiless].forEach((file: any) => {
      const id = uuidv4();
      const fileSizeMB = file.size / (1024 * 1024);

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        newErrors[id] = `File "${file.name}" is not a supported file type.`;
      }

      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        newErrors[id] = `File "${file.name}" exceeds the 100MB size limit.`;
      }

      newFiles.push({
        id,
        file,
        name: file.name,
        size: (file.size / 1024).toFixed(1),
        progress: 0,
        status: "pending",
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrorMsg((prev) => ({ ...prev, ...newErrors }));
    }

    if (newFiles.length > 0) {
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return getIconBasedOnFileExt(ext || "");
  };

  const removeFile = (idToRemove: string) => {
    const newFiles = files.filter((f) => f.id !== idToRemove);
    setFiles(newFiles);
    setErrorMsg((prev) => {
      const newErrors = { ...prev };
      delete newErrors[idToRemove];
      if (newErrors[MAX_FILE_ERROR_KEY] && newFiles.length <= MAX_FILE_COUNT) {
        delete newErrors[MAX_FILE_ERROR_KEY];
      }
      return newErrors;
    });
  };

  const handleOnSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (Object.keys(errorMsg).length > 0) {
        setAlertMessage({
          open: true,
          message: "Please fix the errors before submitting.",
          type: "error",
        });
        return;
      }
      setIsLoading(true);
      const formData = new FormData();
      if (files?.length) {
        files.forEach((f) => {
          formData.append("attachments", f.file);
        });
      }
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("subject", values.subject);
      formData.append("description", values.description);
      formData.append("phone", values.phone);

      const resp = await postSupport(formData);
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          resp?.data?.message || "Support request submitted successfully",
        type: "success",
      });
      resetForm({ values: { ...initialValues, phone: "+1" } });
      getSupportHistory();
      setFiles([]);
      setErrorMsg({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong. Please try again later.",
        type: "error",
      });
    }
  };

  const getSupportHistory = async () => {
    try {
      setIsLoading(true);
      const res = await getSupportList();
      setSupportHistory(res?.data?.data || []);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong while fetching support history.",
        type: "error",
      });
      console.error("Error fetching support history:", error);
    }
  };

  useEffect(() => {
    getSupportHistory();
  }, []);

  return (
    <div className="customer-support-wrapper">
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </div>
      {!isEmailConfigured && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <EmailSetUpBanner />
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          handleOnSubmit(values, { resetForm });
        }}
      >
        {({ values, errors, touched, setFieldValue, resetForm }) => {
          return (
            <Form
              className="customer-support-email-form"
              style={{ flex: 1 }}
              noValidate
            >
              <div className="customer-support-form-header">
                <h1>Support Email</h1>
                <span>Tell us what you need help with.</span>
              </div>
              <div className="customer-support-form-body">
                <div className="form-group">
                  <TextInputComponent
                    type="text"
                    fieldName="name"
                    label="Name"
                    placeholder="Enter Name"
                    value={values.name}
                    onChange={(e: any) =>
                      setFieldValue("name", e?.target?.value)
                    }
                    isName={true}
                    minlength="3"
                    maxlength="50"
                    optional
                    disabled={!canWrite || !isEmailConfigured}
                  />
                  <TextInputComponent
                    type="email"
                    fieldName="email"
                    label="Contact Email"
                    placeholder="Enter Contact Email"
                    value={values.email}
                    onChange={(e: any) =>
                      setFieldValue("email", e?.target?.value)
                    }
                    minlength="3"
                    maxlength="50"
                    optional
                    disabled={!canWrite || !isEmailConfigured}
                  />
                  <div>
                    <CountryCodeWithPhoneNumber
                      fieldName="phonenumber"
                      label="Phone Number"
                      placeholder="Enter Phone Number"
                      onChange={(value: string, country: any) => {
                        const countryCode = country?.dialCode;

                        if (!countryCode) {
                          console.warn("Missing dialCode in country object");
                          return;
                        }

                        const numberOnly = value.slice(countryCode.length);
                        const formatted = `+${countryCode} ${numberOnly}`;

                        setFieldValue("phone", formatted);
                      }}
                      // onBlur={() => {
                      //   setFieldTouched("users[0].phoneNumber", true);
                      // }}
                      value={values?.phone || ""}
                      defaultCountry="us"
                      disabled={!canWrite || !isEmailConfigured}

                      // countryCodeEditable
                    />
                    {errors?.phone && touched?.phone && (
                      <div className="error-message">{errors.phone}</div>
                    )}
                  </div>
                </div>
                <TextInputComponent
                  type="text"
                  fieldName="subject"
                  label="Subject"
                  placeholder="Enter Subject"
                  value={values.subject}
                  onChange={(e: any) =>
                    setFieldValue("subject", e?.target?.value)
                  }
                  minlength="3"
                  maxlength="50"
                  optional
                  disabled={!canWrite || !isEmailConfigured}
                />
                <div className="description-input-wrapper">
                  <label className="description-label">Description *</label>
                  <DescriptionInputComponent
                    fieldName="description"
                    label="Description"
                    placeholder="Explain briefly the issue"
                    value={values.description}
                    onChange={(e: any) =>
                      setFieldValue("description", e?.target?.value)
                    }
                    rows={4}
                    disabled={!canWrite || !isEmailConfigured}
                  />
                </div>
              </div>
              <div
                className="upload-container-wrapper"
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="upload-content">
                  <input
                    type="file"
                    ref={fileInputRef}
                    name="attachments"
                    multiple
                    onChange={handleFileChange}
                    className="file-input"
                    onClick={(e) => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    id="fileInput"
                    hidden
                    disabled={!canWrite || !isEmailConfigured}
                  />
                  <label
                    htmlFor="fileInput"
                    className={`upload-content-wrapper ${!canWrite ? "disabled" : ""}`}
                  >
                    <img
                      src={icons.AttachmentIcon}
                      className="image-icon-wrapper"
                      alt="Upload Icon"
                    />
                    <p className="upload-text-wrapper">Add attachment</p>
                  </label>
                </div>
                <div className="file-list-wrapper">
                  <div className="list-files">
                    {files?.map((f) => (
                      <>
                        <div key={f?.id} className="file-info-wrapper">
                          <div title={f.name} className="file-name">
                            <span className="file-type-icon">
                              {getFileIcon(f.name)}
                            </span>
                            <span>
                              {f.name} ({f.size} KB)
                            </span>
                          </div>
                          <span
                            onClick={() => removeFile(f?.id)}
                            className="remove-icon"
                          >
                            ×
                          </span>
                        </div>
                        {errorMsg[f.id] && (
                          <div className="error-message">{errorMsg[f.id]}</div>
                        )}
                      </>
                    ))}
                    {errorMsg[MAX_FILE_ERROR_KEY] && (
                      <div className="error-message">
                        {errorMsg[MAX_FILE_ERROR_KEY]}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="customer-support-form-footer">
                <button
                  type="button"
                  className="button-outlined"
                  onClick={() => {
                    resetForm({ values: { ...initialValues, phone: "+1" } });
                    setFiles([]);
                    setErrorMsg({});
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  disabled={!canWrite || !isEmailConfigured}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canWrite || !isEmailConfigured}
                  className="submit-button"
                >
                  Submit
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <div className="customer-support-history">
        <div className="customer-support-history-header">
          <h2>Latest Support History</h2>
          <span>View all your recent support emails.</span>
        </div>
        <div className="customer-support-history-content">
          <MUIBoxTable
            columns={headers}
            data={formattedData}
            noResultText={noResultText}
          />
        </div>
      </div>

      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type={alertMessage?.type || "error"}
          open={alertMessage?.open}
          onClose={() => {
            setAlertMessage({ open: false });
          }}
        />
      )}
    </div>
  );
};

export default CustomerSupport;
